/**
 * DeadlinesBrowse — /deadlines
 *
 * Public page: sector-filtered global feed of firm application
 * deadlines. Signed-in students can pin a deadline for 14/3/1-day
 * email reminders.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Loader2, Pin, PinOff, ExternalLink, MapPin, Check } from "lucide-react";
import { useDeadlineImpression, trackDeadlineClick } from "@/hooks/useDeadlineImpression";


// Impression pixel + hover tracking on a single deadline card. All
// state lives in the parent; this just wraps the row so each card
// gets its own IntersectionObserver.
function DeadlineCard({ r, isPinned, isRsvped, togglePin, toggleRsvp, urgent, d, KIND_LABEL }) {
  const impressionRef = useDeadlineImpression(r.id, { source: "deadlines_feed" });
  return (
    <li
      ref={impressionRef}
      className={
        "p-4 rounded-xl border bg-white flex flex-col " +
        (r.sponsored_paid_at
          ? "border-amber-300 ring-1 ring-amber-200/60"
          : "border-stone-200")
      }
      data-testid={`deadlines-card-${r.id}`}
    >
      <div className="flex items-center justify-between text-[11px] font-mono text-stone-500">
        <Link
          to={`/employers/${r.firm_slug}`}
          onClick={() => trackDeadlineClick(r.id, "deadlines_feed_firm")}
          className="font-medium text-stone-900 hover:underline truncate"
        >
          {r.firm_name || r.firm_slug}
        </Link>
        <span className={urgent ? "text-red-600 font-semibold" : ""}>
          {d} day{d === 1 ? "" : "s"} left
        </span>
      </div>
      {r.sponsored_paid_at && (
        <span
          className="mt-1 self-start inline-flex items-center gap-0.5 text-[9px] font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-0.5 uppercase tracking-wide"
          data-testid={`deadlines-sponsored-${r.id}`}
        >
          Sponsored
        </span>
      )}
      <div className="mt-1 font-medium text-stone-900 text-sm">{r.title}</div>
      <div className="mt-0.5 text-[11px] font-mono text-stone-500">
        Closes {new Date(r.deadline_at).toLocaleDateString()} · {KIND_LABEL[r.kind] || r.kind}
        {r.city && <> · {r.city}</>}
        {r.university_name && (
          <> · <Link to={`/uni/${r.university_id}`} className="text-[#4C1D95] hover:underline">{r.university_name}</Link></>
        )}
      </div>
      {r.description && (
        <p className="mt-2 text-[12px] text-stone-600 line-clamp-2">{r.description}</p>
      )}
      <div className="mt-auto pt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant={isPinned ? "secondary" : "outline"}
            size="sm"
            onClick={() => togglePin(r)}
            data-testid={`deadlines-pin-${r.id}`}
          >
            {isPinned ? <PinOff className="h-3.5 w-3.5 mr-1" /> : <Pin className="h-3.5 w-3.5 mr-1" />}
            {isPinned ? "Pinned" : "Pin for reminders"}
          </Button>
          <Button
            variant={isRsvped ? "default" : "outline"}
            size="sm"
            onClick={() => toggleRsvp(r)}
            data-testid={`deadlines-rsvp-${r.id}`}
            className={isRsvped ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
          >
            {isRsvped ? <Check className="h-3.5 w-3.5 mr-1" /> : null}
            {isRsvped ? "Going" : "RSVP"}
          </Button>
        </div>
        {r.apply_url && (
          <a
            href={r.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackDeadlineClick(r.id, "deadlines_feed")}
            className="text-[11px] text-[#4C1D95] hover:underline inline-flex items-center gap-1"
            data-testid={`deadlines-apply-${r.id}`}
          >
            Details <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </li>
  );
}


const KIND_LABEL = {
  // Application deadlines
  grad_scheme: "Grad scheme",
  internship: "Internship",
  spring_insight: "Spring insight",
  open_day: "Open day",
  hackathon: "Hackathon",
  scholarship: "Scholarship",
  // Events (Feb 2026)
  drinks_canapes: "Drinks & canapés",
  meet_and_greet: "Meet & greet",
  presentation: "Presentation",
  panel_discussion: "Panel discussion",
  coffee_chat: "Coffee & conversation",
  competition: "Competition",
  workshop: "Workshop",
  summit: "Summit",
  trading_simulation: "Trading simulation",
  careers_fair: "Careers fair",
  alumni_networking: "Alumni networking",
  case_study_night: "Case-study night",
  insight_day: "Insight day",
};

const SECTORS = [
  "consulting", "finance", "tech", "law", "engineering", "medicine",
  "policy_gov", "media_creative", "science_research", "energy_climate",
  "retail_fmcg", "aerospace_defence", "startup", "nonprofit", "education",
];


function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}


function daysOut(iso) {
  const now = new Date();
  const then = new Date(iso);
  return Math.ceil((then - now) / (1000 * 60 * 60 * 24));
}


export default function DeadlinesBrowse() {
  const { user, openSignIn } = useAuth();
  const [searchParams] = useSearchParams();
  const universityFilter = searchParams.get("university") || "";
  const [universityName, setUniversityName] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("");
  const [kind, setKind] = useState("");
  const [pins, setPins] = useState(new Set());
  const [rsvps, setRsvps] = useState(new Set());
  const [myCities, setMyCities] = useState([]);       // student's target_cities
  const [useMyCities, setUseMyCities] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (sector) q.set("sector", sector);
      if (kind) q.set("kind", kind);
      if (useMyCities && myCities.length) q.set("cities", myCities.join(","));
      if (universityFilter) q.set("university", universityFilter);
      const { data } = await api.get(`/deadlines?${q}`);
      setRows(data.items || []);
      // Cache the university's display name so we can show a filter chip.
      if (universityFilter && !universityName) {
        try {
          const { data: u } = await api.get(`/universities/${universityFilter}`);
          setUniversityName(u?.name || "");
        } catch { /* silent */ }
      }
      if (user) {
        const [{ data: mine }, { data: prof }, { data: myRsvps }] = await Promise.all([
          api.get("/me/deadlines"),
          api.get("/talent/profile/me"),
          api.get("/me/rsvps").catch(() => ({ data: { items: [] } })),
        ]);
        setPins(new Set((mine.pinned || []).map(p => p.deadline_id)));
        setRsvps(new Set((myRsvps.items || []).map(r => r.deadline_id)));
        setMyCities(prof?.profile?.target_cities || []);
      } else {
        setMyCities([]);
        setUseMyCities(false);
        setRsvps(new Set());
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sector, kind, useMyCities, user?.user_id, universityFilter]);

  const togglePin = async (r) => {
    if (!user) { openSignIn?.(); return; }
    try {
      if (pins.has(r.id)) {
        await api.delete(`/me/deadlines/${r.id}/pin`);
        setPins((s) => { const n = new Set(s); n.delete(r.id); return n; });
        toast.success("Unpinned");
      } else {
        await api.post(`/me/deadlines/${r.id}/pin`);
        setPins((s) => new Set([...s, r.id]));
        toast.success("Pinned — you'll get reminders 14/3/1 days out");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleRsvp = async (r) => {
    if (!user) { openSignIn?.(); return; }
    try {
      if (rsvps.has(r.id)) {
        await api.delete(`/deadlines/${r.id}/rsvp`);
        setRsvps((s) => { const n = new Set(s); n.delete(r.id); return n; });
        toast.success("RSVP cancelled");
      } else {
        await api.post(`/deadlines/${r.id}/rsvp`);
        setRsvps((s) => new Set([...s, r.id]));
        toast.success("You're going — firm sees your name");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="deadlines-page">
      <header>
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <CalendarClock className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Live deadline feed
          </span>
        </div>
        <h1 className="mt-1 font-editorial text-3xl sm:text-4xl">
          Application windows closing next
        </h1>
        <p className="mt-2 text-sm text-stone-600 max-w-2xl">
          Real deadlines posted by firms on WUA. Pin any card to get
          three email reminders (14 days, 3 days, 1 day) before it
          closes. {" "}
          {user ? (
            <Link to="/me/talent-profile" className="underline">Manage your reminder preferences.</Link>
          ) : (
            <button onClick={openSignIn} className="underline">Sign in to pin deadlines.</button>
          )}
        </p>
      </header>

      <div className="mt-6 flex flex-wrap gap-2 items-center" data-testid="deadlines-filters">
        {universityFilter && (
          <Link
            to="/deadlines"
            data-testid="deadlines-uni-chip"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95] bg-[#4C1D95] text-white px-3 py-1.5 text-[12px] hover:bg-[#3B0764]"
            title="Clear university filter"
          >
            At {universityName || universityFilter}
            <span className="opacity-80 ml-1">×</span>
          </Link>
        )}
        {user && myCities.length > 0 && (
          <button
            type="button"
            onClick={() => setUseMyCities(v => !v)}
            data-testid="deadlines-my-cities-chip"
            className={
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] transition " +
              (useMyCities
                ? "bg-emerald-700 border-emerald-700 text-white"
                : "bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-50")
            }
            title={`Filter to ${myCities.join(", ")}`}
          >
            <MapPin className="h-3.5 w-3.5" />
            In my target cities · {myCities.length}
            {useMyCities && <span className="opacity-80 ml-1">×</span>}
          </button>
        )}
        <select value={sector} onChange={(e) => setSector(e.target.value)}
                className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm"
                data-testid="deadlines-filter-sector">
          <option value="">Any sector</option>
          {SECTORS.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm"
                data-testid="deadlines-filter-kind">
          <option value="">Any kind</option>
          {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="mt-6" data-testid="deadlines-grid">
        {loading ? (
          <div className="text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-stone-300 rounded-xl text-stone-500 text-sm">
            No open deadlines match this filter yet.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3">
            {rows.map((r) => {
              const d = daysOut(r.deadline_at);
              const urgent = d <= 3;
              return (
                <DeadlineCard
                  key={r.id}
                  r={r}
                  isPinned={pins.has(r.id)}
                  isRsvped={rsvps.has(r.id)}
                  togglePin={togglePin}
                  toggleRsvp={toggleRsvp}
                  urgent={urgent}
                  d={d}
                  KIND_LABEL={KIND_LABEL}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
